import { useI18n } from 'vue-i18n'
import type { BaseError } from '@cplug/frontend-core/models'
import { isCompanyAddressNotConfiguredError } from '../gateways/legalTermGatewayErrors'
import { LegalTermType } from '../models/LegalTerm'
import type { LegalTerm, LegalTermContentType } from '../models/LegalTerm'

export type LegalTermTemplateListErrorKind = 'address_missing' | 'generic' | null

export function isLegalTermTemplatesAddressMissingError (error: BaseError): boolean {
  return isCompanyAddressNotConfiguredError(error)
}

export interface LegalTermFieldErrors {
  text: string[]
  externalLink: string[]
}

export const LEGAL_TERM_TYPE_LABELS: Record<LegalTermType, string> = {
  [LegalTermType.TermsOfUse]: 'TermsOfUse',
  [LegalTermType.PrivacyPolicy]: 'PrivacyPolicy',
  [LegalTermType.DeliveryPolicy]: 'DeliveryPolicy',
  [LegalTermType.ReturnsPolicy]: 'ReturnsPolicy',
  [LegalTermType.Custom]: 'Custom'
}

export function isLegalTermTextEmpty (text?: string | null): boolean {
  if (!text?.trim()) {
    return true
  }

  const plainText = text
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()

  return plainText.length === 0
}

export function isValidLegalTermExternalLink (value: string): boolean {
  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function legalTermHasContent (term: Pick<LegalTerm, 'text' | 'externalLink'>): boolean {
  const hasText = !isLegalTermTextEmpty(term.text)
  const hasExternalLink = (term.externalLink?.trim().length ?? 0) > 0
  return hasText || hasExternalLink
}

export function resolveLegalTermContentType (
  term?: Pick<LegalTerm, 'text' | 'externalLink' | 'activeContentType'>
): LegalTermContentType {
  if (term?.activeContentType) {
    return term.activeContentType
  }

  return (term?.externalLink?.trim().length ?? 0) > 0 ? 'external_link' : 'text'
}

export function validateLegalTermFields (
  term: Pick<LegalTerm, 'text' | 'externalLink'>,
  contentType: LegalTermContentType,
  messages: {
    textRequired: string
    externalLinkRequired: string
    externalLinkInvalid: string
  }
): LegalTermFieldErrors {
  const errors: LegalTermFieldErrors = { text: [], externalLink: [] }
  const link = term.externalLink?.trim() ?? ''

  if (contentType === 'external_link') {
    if (!link) {
      errors.externalLink = [messages.externalLinkRequired]
    } else if (!isValidLegalTermExternalLink(link)) {
      errors.externalLink = [messages.externalLinkInvalid]
    }
    return errors
  }

  if (isLegalTermTextEmpty(term.text)) {
    errors.text = [messages.textRequired]
  }

  return errors
}

export function prepareLegalTermForSave<T extends LegalTerm> (
  term: T,
  contentType: LegalTermContentType
): T {
  if (contentType === 'external_link') {
    return {
      ...term,
      text: '',
      externalLink: term.externalLink?.trim() ?? ''
    }
  }

  return {
    ...term,
    text: term.text ?? '',
    externalLink: undefined
  }
}

type LegalTermHelpers = {
  hasContent: typeof legalTermHasContent
  resolveContentType: typeof resolveLegalTermContentType
  prepareForSave: typeof prepareLegalTermForSave
  isTextEmpty: typeof isLegalTermTextEmpty
  isValidExternalLink: typeof isValidLegalTermExternalLink
  resolveTitle: (type: LegalTermType) => string
  getTypeLabel: (type: LegalTermType) => string
  getTypeDescription: (type: LegalTermType) => string
  getValidationMessages: () => { textRequired: string, externalLinkRequired: string, externalLinkInvalid: string }
  validateFields: (
    term: Pick<LegalTerm, 'text' | 'externalLink'>,
    contentType: LegalTermContentType
  ) => LegalTermFieldErrors
}

export function useLegalTermHelpers (): LegalTermHelpers {
  const { t } = useI18n()

  function resolveTitle (type: LegalTermType): string {
    const labelKey = LEGAL_TERM_TYPE_LABELS[type]
    return labelKey ? t(labelKey) : ''
  }

  function getTypeLabel (type: LegalTermType): string {
    return t(LEGAL_TERM_TYPE_LABELS[type])
  }

  function getTypeDescription (type: LegalTermType): string {
    return t(`${LEGAL_TERM_TYPE_LABELS[type]}Description`)
  }

  function getValidationMessages (): { textRequired: string, externalLinkRequired: string, externalLinkInvalid: string } {
    return {
      textRequired: t('ErrorTextRequired'),
      externalLinkRequired: t('ErrorExternalLinkRequired'),
      externalLinkInvalid: t('ErrorExternalLinkInvalid')
    }
  }

  function validateFields (
    term: Pick<LegalTerm, 'text' | 'externalLink'>,
    contentType: LegalTermContentType
  ): LegalTermFieldErrors {
    return validateLegalTermFields(term, contentType, getValidationMessages())
  }

  return {
    hasContent: legalTermHasContent,
    resolveContentType: resolveLegalTermContentType,
    prepareForSave: prepareLegalTermForSave,
    isTextEmpty: isLegalTermTextEmpty,
    isValidExternalLink: isValidLegalTermExternalLink,
    resolveTitle,
    getTypeLabel,
    getTypeDescription,
    getValidationMessages,
    validateFields
  }
}
